/**
 * advance-warp-gate-links.ts — Warp Gate connection lifecycle pass (v3M18).
 *
 * Per GALAXY.md §3.2 redesign (locked 2026-05-02): Warp Gate
 * connections are explicit player-authored pairs that take 600 ticks
 * to establish before they provide travel. This per-tick pass walks
 * `state.warpGateLinks` and:
 *   - Increments `progressTicks` for every `'building'` link.
 *   - Promotes `'building'` → `'live'` once `progressTicks` hits
 *     `targetTicks`.
 *
 * Sever-on-destroy is handled at the destruction site
 * (`despawnShip` calls `severWarpGateLinksForShip`) rather than here —
 * the per-tick pass should not have to scan ships for liveness.
 *
 * Idempotent on stable state: calling on a state with no `'building'`
 * links is a no-op tail. Cheap: O(N) over `state.warpGateLinks` which
 * is bounded by the number of player-authored connections (small).
 */

import type { GameState } from '../state/types/index';

/**
 * Advance every Warp Gate link by one tick. Mutates `state` in place.
 *
 * Promotion happens at `progressTicks === targetTicks` — the link is
 * `'live'` for every tick at and after that point. This means a 600-
 * tick build completes on tick `creationTick + 600`, not 599.
 */
export function advanceWarpGateLinks(state: GameState): void {
  for (const link of state.warpGateLinks.values()) {
    if (link.status !== 'building') continue;
    link.progressTicks += 1;
    if (link.progressTicks >= link.targetTicks) {
      link.progressTicks = link.targetTicks;
      link.status = 'live';
    }
  }
}
